import { useEffect, useMemo, useRef, useState } from "react";
import type { GfData, GfNav } from "../../types/gifting";
import { GF_TABS } from "./gfUtil";
import { STAGE_NAMES } from "../../lib/giftingRules";

interface Hit {
  key: string;
  kind: "クリエイター" | "エントリー";
  title: string;
  sub: string;
  open: () => void;
}

function norm(s: string | null | undefined): string {
  return (s ?? "").normalize("NFKC").toLowerCase().replace(/^@/, "").trim();
}

/** クリエイター名・ハンドル・案件名・受注番号・商品名で検索（クライアント側のみ・board は PII なし） */
function searchHits(data: GfData, nav: GfNav, rawQuery: string): Hit[] {
  const q = norm(rawQuery);
  if (!q) return [];
  const { creators, entries, campaigns } = data.board;
  const campaignName = new Map(campaigns.map((c) => [c.id, c.name]));
  const creatorName = new Map(creators.map((c) => [c.id, c.name]));

  const creatorHits: Hit[] = creators
    .filter((c) => norm(c.name).includes(q) || c.sns.some((s) => norm(s.handle).includes(q)))
    .slice(0, 5)
    .map((c) => ({
      key: `c-${c.id}`,
      kind: "クリエイター",
      title: c.name,
      sub: c.sns[0] ? `@${c.sns[0].handle.replace(/^@/, "")}${c.genre ? `・${c.genre}` : ""}` : c.genre ?? "",
      open: () => nav.openCreator(c.id),
    }));

  const entryHits: Hit[] = entries
    .filter((e) => {
      const cn = e.campaign_id ? campaignName.get(e.campaign_id) : "";
      return (
        norm(e.ship_order_no).includes(q) ||
        norm(cn).includes(q) ||
        norm(e.product_name).includes(q) ||
        norm(creatorName.get(e.influencer_id)).includes(q)
      );
    })
    .slice(0, 6)
    .map((e) => ({
      key: `e-${e.id}`,
      kind: "エントリー",
      title: `${creatorName.get(e.influencer_id) ?? "—"}（${(e.campaign_id && campaignName.get(e.campaign_id)) || e.product_name}）`,
      sub: `${e.status === "cancelled" ? "中止" : STAGE_NAMES[e.stage]}${e.ship_order_no ? `・${e.ship_order_no}` : ""}`,
      open: () => nav.openEntry(e.id, "deals"),
    }));

  return [...creatorHits, ...entryHits].slice(0, 9);
}

export type GfMoreKind = "templates" | "links" | "help";
const MORE_ITEMS: ReadonlyArray<{ key: GfMoreKind; label: string; needsData: boolean }> = [
  { key: "templates", label: "DMテンプレート", needsData: false },
  { key: "links", label: "計測リンクの成績", needsData: true },
  { key: "help", label: "使い方ヘルプ", needsData: false },
];

export function GiftingHeader({ data, nav, onMore }: { data: GfData | null; nav: GfNav; onMore: (k: GfMoreKind) => void }) {
  const [query, setQuery] = useState("");
  const [focused, setFocused] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!moreOpen) return;
    const close = (e: MouseEvent) => {
      if (moreRef.current && !moreRef.current.contains(e.target as Node)) setMoreOpen(false);
    };
    const esc = (e: KeyboardEvent) => e.key === "Escape" && setMoreOpen(false);
    document.addEventListener("mousedown", close);
    window.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", close);
      window.removeEventListener("keydown", esc);
    };
  }, [moreOpen]);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const hits = useMemo(() => (data ? searchHits(data, nav, query) : []), [data, nav, query]);
  const waiting = data ? data.board.entries.filter((e) => e.stage === 4 && e.status === "waiting_address").length : 0;
  const showDrop = focused && query.trim() !== "";

  const choose = (h: Hit) => {
    h.open();
    setQuery("");
    setFocused(false);
  };

  return (
    <header className="gf-header">
      <div className="gf-header-row">
        <div className="gf-brand">
          <span className="gf-brand-logo">STOQ</span>
          <span className="gf-brand-sub">Gifting</span>
        </div>
        <div className="gf-search-wrap">
          <label className="gf-search">
            <span className="gf-search-label">検索</span>
            <input
              type="search"
              value={query}
              placeholder="クリエイター・案件・受注番号"
              onChange={(e) => setQuery(e.target.value)}
              onFocus={() => {
                if (closeTimer.current) clearTimeout(closeTimer.current);
                setFocused(true);
              }}
              onBlur={() => {
                closeTimer.current = setTimeout(() => setFocused(false), 150);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && hits[0]) choose(hits[0]);
                if (e.key === "Escape") {
                  setQuery("");
                  setFocused(false);
                }
              }}
              aria-label="クリエイター・案件・受注番号を検索"
              disabled={!data}
            />
          </label>
          {showDrop && (
            <div className="gf-search-drop" role="listbox">
              {hits.length === 0 ? (
                <div className="gf-search-none">該当なし</div>
              ) : (
                hits.map((h) => (
                  <button
                    key={h.key}
                    type="button"
                    role="option"
                    aria-selected={false}
                    className="gf-search-hit"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => choose(h)}
                  >
                    <span className="gf-search-hit-main">
                      <span className="gf-search-hit-title">{h.title}</span>
                      <span className="gf-search-hit-sub">{h.sub}</span>
                    </span>
                    <span className="gf-search-hit-kind">{h.kind}</span>
                  </button>
                ))
              )}
            </div>
          )}
        </div>
        <div className="gf-more" ref={moreRef}>
          <button
            type="button"
            className="gf-more-btn"
            aria-haspopup="menu"
            aria-expanded={moreOpen}
            onClick={() => setMoreOpen((v) => !v)}
          >
            その他
          </button>
          {moreOpen && (
            <div className="gf-more-menu" role="menu">
              {MORE_ITEMS.map((m) => (
                <button
                  key={m.key}
                  type="button"
                  role="menuitem"
                  className="gf-pe-menu-item"
                  disabled={m.needsData && !data}
                  onClick={() => {
                    setMoreOpen(false);
                    onMore(m.key);
                  }}
                >
                  {m.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
      <nav className="gf-tabs" aria-label="ギフティングのタブ">
        {GF_TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            className={`gf-tab${nav.state.tab === t.key ? " is-active" : ""}`}
            onClick={() => nav.set({ tab: t.key })}
          >
            {t.label}
            {t.key === "ship" && waiting > 0 && <span className="gf-tab-count">{waiting}</span>}
          </button>
        ))}
      </nav>
    </header>
  );
}
